import { useTranslations } from '@power-core/toolkit';
import type { FunctionComponent } from 'react';

import { AbbChevronRight } from '@/assets/icons/components/abb-right';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { LoadingIndicator } from '@/ui-components/loading-indicator/loading-indicator';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import { useDocumentsListInit } from '../../hooks/use-documents-list-init';
import { DocumentRow } from '../document-row/document-row';
import type { DocumentListProps } from '../types';

export const DocumentList: FunctionComponent<DocumentListProps> = ({ documentStatuses, projectId, isOpen, projectsOptions }) => {
	const t = useTranslations();
	const { searchStatusId, setSelectedDocument } = useAppContext();
	const { projectDocuments, isPending } = useDocumentsListInit({ projectId, statusId: searchStatusId, isProjectOpen: isOpen });
	const { isDarkTheme } = useTheme();

	const sortedDocuments = projectDocuments?.sort((a, b) => {
		const timeA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
		const timeB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
		return timeB - timeA;
	});

	if (!isOpen) return null;

	if (isPending) {
		return (
			<Flexbox justify='center' className='mt-5 mb-5'>
				<LoadingIndicator sizing='large' color='primary' />
			</Flexbox>
		);
	}

	if (sortedDocuments && sortedDocuments.length > 0) {
		return (
			<div className='bg-white dark:bg-card_dark rounded-lg m-1'>
				{sortedDocuments.map((document) => (
					<DocumentRow
						key={document.documentId}
						document={document}
						documentStatuses={documentStatuses}
						showHistory={setSelectedDocument}
						projectsOptions={projectsOptions}
					/>
				))}
			</div>
		);
	}

	return (
		<Flexbox items='center' justify='between' className='p-2'>
			<Paragraph className='text-n500_light_text'>{t('T_SELECT_TOOL')}</Paragraph>
			<AbbChevronRight size={24} fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'} className='mr-1' />
		</Flexbox>
	);
};
